import * as Sentry from '@sentry/react';

// Sentry DSN for admin panel
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN_ADMIN_PANEL || '';

// Determine if Sentry should be enabled based on environment
const isSentryEnabled = () => {
  const currentEnv = import.meta.env.VITE_CURRENT_ENVIRONMENT || import.meta.env.MODE || 'development';
  const sentryEnabled = import.meta.env.VITE_SENTRY_ENABLED_ADMIN_PANEL;
  
  // Always enable in production
  if (currentEnv === 'production') {
    return true;
  }
  
  // For other environments, check the SENTRY_ENABLED flag
  return sentryEnabled === 'true';
};

// Debug logging
if (import.meta.env.DEV) {
  console.log('🔍 Sentry Debug Info:', {
    environment: import.meta.env.VITE_CURRENT_ENVIRONMENT || import.meta.env.MODE,
    enabled: isSentryEnabled(),
    hasCustomFlag: !!import.meta.env.VITE_SENTRY_ENABLED_ADMIN_PANEL
  });
}

// Initialize Sentry
Sentry.init({
  dsn: isSentryEnabled() ? SENTRY_DSN : '',
  
  // Add environment tag
  environment: import.meta.env.VITE_CURRENT_ENVIRONMENT || import.meta.env.MODE || 'development',
  
  // Only initialize Sentry if it's enabled
  enabled: isSentryEnabled(),
  
  // Define how likely traces are sampled. Use lower sampling in production
  tracesSampleRate: import.meta.env.VITE_CURRENT_ENVIRONMENT === 'production' ? 0.1 : 1.0,
  
  // Configure performance monitoring
  profilesSampleRate: import.meta.env.VITE_CURRENT_ENVIRONMENT === 'production' ? 0.1 : 1.0,
  
  // Add release information if available
  release: import.meta.env.VITE_VERSION || 'unknown',
  
  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: import.meta.env.DEV || import.meta.env.VITE_CURRENT_ENVIRONMENT !== 'production',
  
  // Configure the SDK with proper user privacy filters for HIPAA compliance
  beforeSend(event) {
    // Skip processing if Sentry is disabled
    if (!isSentryEnabled()) {
      return null;
    }
    
    // Sanitize user data to comply with HIPAA
    if (event.user) {
      // Keep only non-PII information
      delete event.user.ip_address;
      // For Admin Panel: Keep only email, remove ID and other PII
      const userEmail = event.user.email;
      event.user = { email: userEmail };
    }
    
    // Remove PII from custom data
    if (event.extra) {
      // Remove fields that might contain PII
      delete event.extra.email;
      delete event.extra.phone;
      delete event.extra.address;
      delete event.extra.name;
    }
    
    return event;
  },
  
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
    }),
  ],
});

// Log successful initialization
if (isSentryEnabled()) {
  console.log('✅ Sentry initialized successfully for admin panel');
} else {
  console.log('⚠️ Sentry is disabled for this environment');
}

/**
 * Sets the user context in Sentry for admin panel users
 * @param {Object|null} user - The user object containing admin user information
 * @param {string} [user.email] - The user email
 */
export function setSentryUser(user) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    email: user.email || 'unknown@example.com',
  });
}

/**
 * Adds a breadcrumb for authentication events
 * @param {string} action - The authentication action (login, logout, etc.)
 * @param {string} [userId] - The user ID (optional)
 */
export function addAuthBreadcrumb(action, userId) {
  Sentry.addBreadcrumb({
    category: 'auth',
    message: `User ${action}`,
    level: 'info',
    data: {
      action,
      userId: userId || 'unknown',
    },
  });
}

/**
 * Captures an error with additional context
 * @param {Error} error - The error to capture
 * @param {Object} [context] - Additional context information
 */
export function captureErrorWithContext(error, context) {
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(error);
  });
}

/**
 * Sets custom tags for admin-specific context
 * @param {Object} tags - Key-value pairs of tags to set
 */
export function setSentryTags(tags) {
  Object.entries(tags).forEach(([key, value]) => {
    Sentry.setTag(key, value);
  });
}

export default Sentry;